import {Binding, inputBinding, outputBinding, Type} from '@angular/core';
import {ExportColumnId} from '../shared/models/export-columns.model';
import {FlowSlot, flowSlots} from '../shared/models/export-flow-slots.model';
import {OptionItem} from '../shared/ui/option-card/option-card.model';
import {exportFormats} from '../shared/models/output-format.model';
import {SavedExportUsage} from '../saved-exports/saved-export.model';
import {
    ColumnMove,
    ColumnRename,
    ColumnsStepComponent,
} from '../steps/columns-step/columns-step.component';
import {FilePreviewComponent} from '../file-preview/file-preview.component';
import {FileFormatStepComponent} from '../steps/file-format-step/file-format-step.component';
import {NameStepComponent} from '../steps/name-step/name-step.component';
import {ReadingScopeInfoStepComponent} from '../steps/reading-scope-info-step/reading-scope-info-step.component';
import {ReadingShapeStepComponent} from '../steps/reading-shape-step/reading-shape-step.component';
import {ScheduleStepComponent} from '../steps/schedule-step/schedule-step.component';
import {MeterTypesStepComponent} from '../steps/meter-types-step/meter-types-step.component';
import {SystemsStepComponent} from '../steps/systems-step/systems-step.component';
import {UsageStepComponent} from '../steps/usage-step/usage-step.component';
import {CustomizedExportsFormService} from './customized-exports-form.service';

export type CustomizedSlot = Exclude<FlowSlot, 'readingDate'>;

export type CustomizedFlowContext = {
    form: CustomizedExportsFormService;
};

type SlotDefinition = {
    titleKey: string;
    render: Type<unknown>;
    bind: (context: CustomizedFlowContext) => ReadonlyArray<Binding>;
};

export type CustomizedFlowSlot = SlotDefinition & {id: CustomizedSlot};

// The usage cards reuse the quick-exports option texts: on-demand matches "readings on a specific
// date", schedule matches "latest readings on a schedule" (AGI-18790 team test).
export const usageOptions: ReadonlyArray<OptionItem> = [
    {
        id: 'on-demand',
        titleKey: 'quickexports_export_option_specific_date_title',
        descriptionKey: 'quickexports_export_option_specific_date_desc',
        noteKey: 'customizedexports_export_usage_on_demand_info',
    },
    {
        id: 'schedule',
        titleKey: 'quickexports_export_option_schedule_title',
        descriptionKey: 'quickexports_export_option_schedule_desc',
    },
    {
        id: 'customized',
        titleKey: 'customizedexports_export_usage_customized_title',
        descriptionKey: 'customizedexports_export_usage_customized_desc',
    },
];

const slotDefinitions: Record<CustomizedSlot, SlotDefinition> = {
    name: {
        titleKey: 'customizedexports_export_step_name_title',
        render: NameStepComponent,
        bind: ({form}) => [inputBinding('control', () => form.form.controls.name)],
    },
    usage: {
        titleKey: 'quickexports_export_step_what_to_do_title',
        render: UsageStepComponent,
        bind: ({form}) => [
            inputBinding('options', () => usageOptions),
            inputBinding('selectedId', () => form.selectedUsage()),
            outputBinding<string>('optionSelected', id => form.selectUsage(id)),
        ],
    },
    readingScopeInfo: {
        titleKey: 'customizedexports_export_step_reading_scope_info_title',
        render: ReadingScopeInfoStepComponent,
        bind: () => [],
    },
    readingShape: {
        titleKey: 'customizedexports_export_step_reading_shape_title',
        render: ReadingShapeStepComponent,
        bind: ({form}) => [
            inputBinding('readingResolution', () => form.readingResolution()),
            inputBinding('daysBackInTimeControl', () => form.daysBackInTimeControl),
            outputBinding<string>('resolutionSelected', id => form.selectReadingResolution(id)),
        ],
    },
    meterTypes: {
        titleKey: 'customizedexports_export_step_supertypes_title',
        render: MeterTypesStepComponent,
        bind: ({form}) => [
            inputBinding('includeDismountedControl', () => form.includeDismountedMetersControl),
            inputBinding('meterTypes', () => form.meterTypeCards()),
            inputBinding('meterScope', () => form.meterScope()),
            outputBinding<string>('meterTypeToggled', meterSuperTypeNo =>
                form.toggleSuperType(meterSuperTypeNo),
            ),
            outputBinding<void>('allSelected', () => form.selectAllSuperTypes()),
            outputBinding<void>('cleared', () => form.clearSuperTypes()),
            outputBinding<string>('modelToggled', modelId => form.toggleModel(modelId)),
            outputBinding<string[]>('modelsSelected', modelIds => form.selectModels(modelIds)),
            outputBinding<string[]>('modelsDeselected', modelIds => form.deselectModels(modelIds)),
            outputBinding<string>('channelToggled', channelId => form.toggleChannel(channelId)),
            outputBinding<string[]>('channelsSelected', channelIds =>
                form.selectChannels(channelIds),
            ),
            outputBinding<string[]>('channelsDeselected', channelIds =>
                form.deselectChannels(channelIds),
            ),
            outputBinding<string>('channelLessToggled', meterSuperTypeNo =>
                form.toggleChannelLess(meterSuperTypeNo),
            ),
        ],
    },
    systems: {
        titleKey: 'customizedexports_export_step_systems_title',
        render: SystemsStepComponent,
        bind: ({form}) => [
            inputBinding('systems', () => form.relevantSystems()),
            inputBinding('noSuperTypesSelected', () => form.selectedSuperTypes().length === 0),
            inputBinding('autoIncludeControl', () => form.autoIncludeFutureSystemsControl),
            inputBinding('offersAutoInclude', () => form.deliversOnSchedule()),
            inputBinding('selectedSystemIds', () => form.selectedSystemIds()),
            outputBinding<string>('systemToggled', systemNo => form.toggleSystem(systemNo)),
            outputBinding<string[]>('systemsSelected', systemNos =>
                form.selectAllSystems(systemNos),
            ),
            outputBinding<string[]>('systemsDeselected', systemNos =>
                form.deselectSystems(systemNos),
            ),
            outputBinding<void>('cleared', () => form.clearSystems()),
        ],
    },
    format: {
        titleKey: 'quickexports_export_step_format_title',
        render: FileFormatStepComponent,
        bind: ({form}) => [
            inputBinding('formats', () => exportFormats),
            inputBinding('selectedId', () => form.selectedFormat().id),
            inputBinding('fileFormat', () => form.fileFormat),
            inputBinding('settings', () => form.fileFormatSettings()),
            inputBinding('carriesNumbers', () => form.fileCarriesNumbers()),
            inputBinding('carriesDates', () => form.fileCarriesDates()),
            inputBinding('carriesTimestamps', () => form.fileCarriesTimestamps()),
            outputBinding<string>('formatSelected', id => form.selectFormatById(id)),
            outputBinding<void>('settingsReset', () => form.resetFileFormatStep()),
        ],
    },
    columns: {
        titleKey: 'customizedexports_export_step_columns_title',
        render: ColumnsStepComponent,
        bind: ({form}) => [
            inputBinding('headerRow', () => form.headerRow()),
            inputBinding('unitPlacement', () => form.unitPlacement()),
            inputBinding('columns', () => form.orderedColumns()),
            outputBinding<string>('headerRowSelected', id => form.selectHeaderRow(id)),
            outputBinding<string>('unitPlacementSelected', id => form.selectUnitPlacement(id)),
            outputBinding<void>('settingsReset', () => form.resetColumnsStep()),
            outputBinding<ColumnMove>('columnMoved', move =>
                form.moveVisibleColumn(move.from, move.to),
            ),
            outputBinding<ExportColumnId>('columnToggled', id => form.toggleColumn(id)),
            outputBinding<ColumnRename>('columnRenamed', rename =>
                form.renameColumn(rename.id, rename.header),
            ),
        ],
    },
    preview: {
        titleKey: 'customizedexports_export_preview_title',
        render: FilePreviewComponent,
        bind: ({form}) => [
            inputBinding('content', () => form.previewContent()),
            inputBinding('fileName', () => form.previewFileName()),
        ],
    },
    schedule: {
        titleKey: 'customizedexports_export_step_schedule_title',
        render: ScheduleStepComponent,
        bind: ({form}) => [
            inputBinding('schedule', () => form.form.controls.schedule),
            inputBinding('customizedSection', () => true),
            inputBinding('daysBackInTime', () =>
                form.shapesReadings() ? form.daysBackInTime() : null,
            ),
            inputBinding('latestKnownReadings', () => !form.shapesReadings()),
            inputBinding('sftpFileName', () => form.sftpPreviewFileName()),
        ],
    },
};

export function slotIdsForUsage(usage: SavedExportUsage): ReadonlyArray<CustomizedSlot> {
    switch (usage) {
        case 'on-demand':
            return flowSlots(['name', 'usage', 'readingScopeInfo', 'meterTypes', 'systems'], {
                without: ['schedule'],
            });
        case 'schedule':
            return flowSlots(['name', 'usage', 'readingScopeInfo', 'meterTypes', 'systems']);
        case 'customized':
            return flowSlots(['name', 'usage', 'readingShape', 'meterTypes', 'systems']);
    }
}

export function slotsForIds(ids: ReadonlyArray<CustomizedSlot>): ReadonlyArray<CustomizedFlowSlot> {
    return ids.map(id => ({id, ...slotDefinitions[id]}));
}

export function slotsForUsage(usage: SavedExportUsage): ReadonlyArray<CustomizedFlowSlot> {
    return slotsForIds(slotIdsForUsage(usage));
}
